import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../services/api';

const AdminDashboardPage = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const response = await adminAPI.getStats();
      return response.data;
    },
    staleTime: 30000,
  });

  const users = data?.users || {};
  const projects = data?.projects || {};
  const engagement = data?.engagement || {};

  const activeUserRatio =
    users.total && users.active
      ? Math.round((users.active / users.total) * 100)
      : null;

  const publicProjectRatio =
    projects.total && projects.public
      ? Math.round((projects.public / projects.total) * 100)
      : null;

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Admin dashboard
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            High-level overview of users, projects, and engagement.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-sm">
            Loading statistics...
          </div>
        </div>
      ) : isError ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Failed to load admin statistics.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Users */}
            <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Users
              </h2>
              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Total
                  </span>
                  <span className="text-lg font-semibold">
                    {users.total ?? '–'}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Active
                  </span>
                  <span className="text-sm font-medium">
                    {users.active ?? '–'}
                    {activeUserRatio !== null && (
                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                        ({activeUserRatio}%)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Superusers
                  </span>
                  <span className="text-sm font-medium">
                    {users.superusers ?? '–'}
                  </span>
                </div>
              </div>
            </div>

            {/* Projects */}
            <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Projects
              </h2>
              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Total
                  </span>
                  <span className="text-lg font-semibold">
                    {projects.total ?? '–'}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Personal
                  </span>
                  <span className="text-sm font-medium">
                    {projects.personal ?? '–'}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Templates
                  </span>
                  <span className="text-sm font-medium">
                    {projects.templates ?? '–'}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Public
                  </span>
                  <span className="text-sm font-medium">
                    {projects.public ?? '–'}
                    {publicProjectRatio !== null && (
                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                        ({publicProjectRatio}%)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Active
                  </span>
                  <span className="text-sm font-medium">
                    {projects.active ?? '–'}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Finished
                  </span>
                  <span className="text-sm font-medium">
                    {projects.finished ?? '–'}
                  </span>
                </div>
              </div>
            </div>

            {/* Engagement */}
            <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Engagement
              </h2>
              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Reviews
                  </span>
                  <span className="text-lg font-semibold">
                    {engagement.reviewsCount ?? '–'}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Average rating
                  </span>
                  <span className="text-sm font-medium">
                    {engagement.averageRating
                      ? engagement.averageRating.toFixed(1)
                      : '–'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboardPage;


